/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Deck } from 'components/deck'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import * as d3 from 'd3'
import { Link } from '@ta-interaktiv/contentbricks'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

// TODO Set the correct article ID here
const ARTICLE_ID = '189534084317'
const SHEETID =
  '2PACX-1vRV7Fbw1xP9zon5TmAIoVIdMrzkH0mvAptIH4Y989y2PCRiy0PcgyYRCE1GNKLDK-Lkj9JTzsqdm_U-'

export function AppComponent() {
  const [absurdites, setAbsurdites] = useState([])
  const [expandedIntro, setExpandedIntro] = useState(false)
  const isMobile = window.innerWidth < 768

  useEffect(() => {
    d3.csv(
      `https://docs.google.com/spreadsheets/d/e/${SHEETID}/pub?output=csv`
    ).then(data => {
      console.log(data)
      setAbsurdites(data)
    })
  }, [])

  const sendMail = () => {
    const message =
      document.getElementById('mail-body').value +
      '\n\n' +
      document.getElementById('mail-name').value
    document.location.href =
      'mailto:mathieu.rudaz@24heures.ch?subject=' +
      'Coronabsurdités - contribution lecteur' +
      '&body=' +
      encodeURIComponent(message)
  }

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Face au covid, l'incohérence se fait sentir</h1>
            <h2>
              Par le biais d'une fiction, nous avons décrit et compilé les{' '}
              <Link href='752654969451'>incompréhensions</Link> les plus
              fréquentes face aux mesures pour freiner l'épidémie. Cela vous
              interpelle aussi? Partagez-nous vos expériences.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Julien Wicky</div>
            <div className='ta updated'>Mis à jour le 31 janvier 2021</div>
          </div>
          <p>
            C’est l’histoire d’Alfred. Alfred n’existe pas vraiment, mais il y a
            chez lui un peu de vous et de nous. Surtout, ce quadragénaire a la
            fâcheuse tendance à cumuler tous nos petits problèmes. Depuis une
            année, il se conforme aux restrictions pour enrayer la pandémie,
            mais, lessivé comme un marathonien qui s’est mangé le mur du
            trentième kilomètre, il ne voit plus que les problèmes et hésite à
            abandonner. Car au début, on lui a dit que pour adhérer à des
            mesures, il fallait qu’il les comprenne. Et aujourd’hui, il n’arrive
            plus à suivre.{' '}
          </p>
          {!isMobile && (
            <p>
              Vendredi dernier, en quittant son bureau bernois pour rentrer dans
              son quatre-pièces neuchâtelois, il a pris son train de 17 h 53.
              Professeur au secondaire, le télétravail, ce n’est pas possible
              pour lui. Dans son wagon, son masque lui fait mal aux oreilles
              tandis qu’à côté de lui, trois jeunes trinquent aux canettes de
              bière, masque sous le menton parce que boire et manger dans les
              transports, c’est autorisé. D’abord jaloux, Albert finit par
              sourire, il se mettrait bien à leur place.
            </p>
          )}
          {expandedIntro && (
            <>
              {isMobile && (
                <p>
                  Vendredi dernier, en quittant son bureau bernois pour rentrer
                  dans son quatre-pièces neuchâtelois, il a pris son train de 17
                  h 53. Professeur au secondaire, le télétravail, ce n’est pas
                  possible pour lui. Dans son wagon, son masque lui fait mal aux
                  oreilles tandis qu’à côté de lui, trois jeunes trinquent aux
                  canettes de bière, masque sous le menton parce que boire et
                  manger dans les transports, c’est autorisé. D’abord jaloux,
                  Albert finit par sourire, il se mettrait bien à leur place.
                </p>
              )}
              <p>
                Il pense à ces mêmes ados qu’il a tous les jours en face de lui
                et dont il ne distingue plus les sourires. Sauf à midi,
                lorsqu’il voit tomber leurs masques pour aller s’acheter une
                salade, un sandwich, un kebab ou un McDo qu’ils dévorent
                ensemble en classe. «Bonjour les incohérences», pense-t-il. À
                mesure que le paysage défile, il cogite. «Je peux acheter des
                fleurs mais pas le vase pour les entreposer, des capsules à café
                mais pas de nouvelle machine pour remplacer l’ancienne qui est
                entartrée, des poêles mais pas de linges de cuisine. Oh, et puis
                je dois passer deux heures par jour dans ce train bondé, mais je
                ne peux pas aller me racheter des chemises dans cette petite
                boutique du centre-ville où Jean-Pierre, le patron, pourrait
                limiter la présence à deux personnes.»
              </p>
              <p>
                Alfred n’est pas du genre à souhaiter que tout soit fermé. Il
                n’a rien contre les coiffeurs, instituts de beauté ou ongleries.
                Au fond, tant mieux pour eux, mais il ne voit pas pourquoi on
                peut passer une heure à se refaire un brushing et ne pas pouvoir
                aller s’offrir son dernier Stephen King dans sa librairie de
                quartier. Enrichir Amazon, très peu pour lui. Et l’odeur des
                livres, ça lui manque. En rentrant, il a encore pu s’acheter ses
                bières belges, mais pas ses protéines pour son fitness. La
                première échoppe avait le droit d’ouvrir, pas la seconde. Bref,
                il fatigue. D’ailleurs, ce soir-là, il est tombé comme une
                masse.
              </p>
              <p>
                Ce samedi matin, il fait beau. Alfred et son épouse Brigitte ont
                décidé d’aller skier, de préférence en Valais parce que les
                stations vaudoises et du Jura sont bondées. Lisant son quotidien
                régional, il se sent poursuivi. On lui apprend que les commerces
                de laines et tissus ont dû fermer. «Par contre, les clous, les
                planches et les marteaux, pas de souci», soupire-t-il. Bref,
                direction la montagne. Se sentant un brin coupables d’aller se
                presser dans les télécabines, ils se consolent en se disant
                qu’il n’y a rien d’autre à faire. Mais le rappel à l’ordre ne
                tarde pas à venir du fils aîné, Jérôme. «Dis papa, tu trouves
                pas ça idiot qu’on puisse aller skier, mais pas jouer au tennis
                à 20 mètres l’un de l’autre?» Et sa cadette, Juliette, d’en
                remettre une couche. «Moi, à 15 ans, je peux jouer au tennis.»
              </p>
              <p>
                Arrivé au pied des installations, Jérôme a oublié ses gants. «On
                va aller t’en acheter, je dois de toute façon en profiter pour
                m’acheter de nouveaux skis», lui dit son père. «Pas possible»,
                lui rétorque, embarrassé, un vendeur. «Je peux vous en louer, il
                faudra les ramener ce soir, mais pas vous en vendre, et pour les
                gants… on n’a pas le droit. Par contre, si vous allez dans un
                magasin de jardinage, vous en trouverez. Ou alors vous ressortez
                et vous faites un click & collect.» Alfred est au bord de la
                crise de nerfs. Après le kiosquier qui lui a vendu son journal
                mais pas «Le Petit Spirou» pour le petit dernier parce que
                «c’est du divertissement et c’est interdit», c’en est trop.
              </p>
              <p>
                Prêt à faire demi-tour, il devine de la lumière dans l’hôtel du
                coin. Le spa est ouvert aux clients, le restaurant aussi.
                Prenant le dessus sur ses dilemmes intérieurs, sa santé mentale
                le pousse à l’intérieur pour le week-end. Parce qu’Alfred, comme
                vous, comme nous, il n’en peut plus de ce marathon. Alfred n’est
                pas un contestataire, il se plie aux mesures, mais il a déjà eu
                l’impression d’en faire beaucoup. Et parce qu’à la fin, la
                différence se fait au mental, il aimerait juste se ravitailler
                de façon cohérente.
              </p>
            </>
          )}
          <div
            className={`read-more ${expandedIntro ? 'expanded' : ''}`}
            onClick={() => {
              setExpandedIntro(!expandedIntro)
            }}
          >
            <p className='ta ui text'>
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                {expandedIntro ? 'Réduire le texte' : 'Continuer la lecture'}
              </span>{' '}
              <i
                className={`ui icon chevron ${expandedIntro ? 'up' : 'down'}`}
              />
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text center aligned container'>
          <div className='howto'>
            <img src={require('../../images/swipe_icon.svg')} />
            <div className='ui small'>
              Swipez pour faire défiler ou envoyez-nous votre{' '}
              <span style={{ textDecoration: 'underline' }}>
                propre anecdote
              </span>
            </div>
          </div>
        </div>
        <div id='root'>
          <Deck data={absurdites} />
        </div>
      </div>

      {/* <div className='ui vertical segment'>
        <div className='ui text container'>
          <TypescriptComponent data={absurdites} />
        </div>
  </div> */}

      <div className='ui vertical grey segment'>
        <div className='ui text container contribute'>
          <h4>Contribuez</h4>
          <form
            action='mailto:mathieu.rudaz@24heures.ch'
            method='get'
            encType='text/plain'
          >
            <p>
              Et <b>vous</b>, quelles sont vos anecdotes? Envoyez-nous les
              situations les plus <b>absurdes</b> provoquées par les
              restrictions anti-Covid.
              <br />
              <textarea id='mail-body' name='body' rows={12} cols={35} />
            </p>
            <p>
              Votre nom
              <br />
              <input id='mail-name' type='text' name='name' />
            </p>
            {/* <p>
              <input type='submit' name='submit' value='Envoyer' onClick={sendMail} />
            </p> */}
          </form>
          <input
            type='submit'
            name='submit'
            value='Envoyer'
            onClick={sendMail}
          />
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui container'>
          <h4>Plus d'articles sur ce thème</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={[
              '752654969451',
              '887708527605',
              '502521269665',
              '361703326871'
            ]}
          />
        </div>
      </div>

      <Footer
        mailTo='elisa.blondel@lematindimanche.ch '
        locale='fr'
        hidePortfolioTeaser
        articleId={ARTICLE_ID}
        credits={[
          ['Textes', 'Julien Wicky'],
          ['Conception et réalisation', 'Mathieu Rudaz']
        ]}
      />
    </div>
  )
}
